import type { fetchCalendars } from '@shared/utils/connections/calendars'

/** The user's calendar list, as returned by {@link fetchCalendars}. */
type Calendars = Awaited<ReturnType<typeof fetchCalendars>>

/**
 * Resolve the connectionId to query free/busy under, given the user's calendar
 * list, the selected calendar id, and the draft action's `provider`.
 *
 *  - An EXPLICIT, non-primary calendar id must resolve to its OWN connection: if
 *    it isn't in the list we return null (→ availability "unknown") rather than
 *    guessing, since a wrong connection yields misleading free/busy.
 *  - For the `'primary'` sentinel (or an absent id) we fall back to a primary
 *    calendar — but SCOPED to the action's `provider`. The draft was produced by
 *    a specific toolset (e.g. `google_calendar`), so its availability must be
 *    queried under a matching-provider connection. Falling back to the GLOBAL
 *    primary would, for a Google draft, happily resolve to e.g. a personal
 *    Microsoft account whose Graph `getSchedule` can't see the (Google) guests —
 *    every attendee then comes back unknown (observed: Outlook error 5009). When
 *    the user has no calendar for that provider we fall back to the global
 *    primary rather than failing outright.
 *
 * Returns null when the user has no calendars.
 */
export function resolveConnectionId(
  calendars: Calendars,
  calendarId: string | undefined,
  provider?: 'google' | 'microsoft',
): string | null {
  if (calendars.length === 0) return null
  if (calendarId && calendarId !== 'primary') {
    const match = calendars.find((c) => c.id === calendarId)
    return match ? match.connectionId : null
  }
  // 'primary' / unselected → prefer the action provider's calendars so we don't
  // resolve a Google draft to a Microsoft account (or vice versa).
  const scoped = provider ? calendars.filter((c) => c.providerName === provider) : []
  const pool = scoped.length > 0 ? scoped : calendars
  const primary = pool.find((c) => c.isPrimary)
  return (primary ?? pool[0]).connectionId
}
